import { useMemo, useState } from 'react';
import {
  Button,
  Card,
  Drawer,
  Grid,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Tag,
  Tooltip,
} from 'antd';
import { Languages, ListFilter, Pencil, Plus, Settings2, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useDebounce } from '@shared/hooks/useDebounce';
import { DataTable } from '@shared/components/DataTable';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { StatusBadge } from '@shared/components/StatusBadge';
import { translationsApi } from '@api/translations';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { recordAuditAction } from '@features/audit/recordAction';
import { useActiveLanguages } from '@features/languages/useLanguages';
import { TranslationDrawer, type TranslationSeed } from '../components/TranslationDrawer';
import { NamespaceManagerDrawer } from '../components/NamespaceManagerDrawer';
import { LanguageCoverageTags } from '../components/LanguageCoverageTags';
import { useNamespaces } from '../useNamespaces';
import type { TranslationKey, TranslationListQuery, TranslationStatus } from '../types';

type SortKey = NonNullable<TranslationListQuery['sort']>;
type StatusFilter = TranslationStatus | 'ALL';

export function TranslationsPage() {
  const { t } = useTranslation(['translations', 'common', 'validation', 'errors']);
  const qc = useQueryClient();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [sort, setSort] = useState<SortKey>('key');
  const [namespace, setNamespace] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [languageFilter, setLanguageFilter] = useState<string>('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<TranslationKey | null>(null);
  const [seed, setSeed] = useState<TranslationSeed | null>(null);
  const [nsDrawerOpen, setNsDrawerOpen] = useState(false);
  const [nsManagerOpen, setNsManagerOpen] = useState(false);

  const { data: languages = [] } = useActiveLanguages();
  const activeCodes = useMemo(() => languages.map((l) => l.code), [languages]);

  const { data, isFetching } = useQuery({
    queryKey: [
      'translations',
      { page, pageSize, q: debouncedSearch, sort, namespace, status: statusFilter, lang: languageFilter },
    ],
    queryFn: () =>
      translationsApi.list({
        page,
        pageSize,
        q: debouncedSearch || undefined,
        sort,
        namespace: namespace || undefined,
        languageCode: languageFilter || undefined,
        status: statusFilter === 'ALL' ? undefined : statusFilter,
      }),
    placeholderData: (prev) => prev,
  });

  const items = useMemo(() => data?.items ?? [], [data]);

  // Namespace directory (keyCount + present languages) from the dedicated endpoint.
  const { data: namespaceSummaries = [], isFetching: nsFetching } = useNamespaces();
  const namespaceNames = useMemo(
    () => namespaceSummaries.map((s) => s.namespace),
    [namespaceSummaries],
  );

  const deleteMutation = useMutation({
    mutationFn: (row: TranslationKey) => translationsApi.remove(row.id),
    onSuccess: async (_d, row) => {
      toast.success(t('toast.deleted', { key: row.key }));
      await qc.invalidateQueries({ queryKey: ['translations'] });
      void recordAuditAction({
        action: 'translation.delete',
        resource: 'translation',
        resourceId: row.id,
        summary: t('audit.delete', { key: row.key }),
      });
    },
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err));
    },
  });

  const openCreate = () => {
    setEditing(null);
    setSeed(null);
    setDrawerOpen(true);
  };

  const openEdit = (row: TranslationKey) => {
    setEditing(row);
    setSeed(null);
    setDrawerOpen(true);
  };

  // "Dil ekle": aynı (namespace, key) için, satırın dilinden farklı bir aktif dili
  // ön-seç (yoksa satırın dili) — drawer create modunda kilitli ns+key ile açılır.
  const openAddLanguage = (row: TranslationKey) => {
    const target =
      activeCodes.find((c) => c.toLowerCase() !== row.languageCode.toLowerCase()) ??
      row.languageCode;
    setEditing(null);
    setSeed({ namespace: row.namespace, key: row.key, languageCode: target });
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditing(null);
    setSeed(null);
  };

  const columns = useMemo(
    () => [
      {
        key: 'namespace',
        title: t('table.columns.namespace'),
        dataIndex: 'namespace',
        width: 150,
        render: (v: string) => (
          <span style={{ fontSize: 12, color: 'var(--ds-text-secondary)' }}>{v}</span>
        ),
      },
      {
        key: 'key',
        title: t('table.columns.key'),
        dataIndex: 'key',
        render: (v: string) => (
          <code style={{ fontSize: 12, color: 'var(--ds-text-primary)' }}>{v}</code>
        ),
      },
      {
        key: 'language',
        title: t('table.columns.language'),
        dataIndex: 'languageCode',
        width: 80,
        render: (code: string) => (
          <Tag color="blue" style={{ margin: 0, textTransform: 'uppercase' }}>
            {code}
          </Tag>
        ),
      },
      {
        key: 'value',
        title: t('table.columns.value'),
        dataIndex: 'value',
        responsive: ['md' as const],
        render: (v: string) =>
          v ? (
            <Tooltip title={v}>
              <span
                style={{
                  display: 'inline-block',
                  maxWidth: 320,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  verticalAlign: 'bottom',
                }}
              >
                {v}
              </span>
            </Tooltip>
          ) : (
            <span style={{ color: 'var(--ds-text-tertiary)', fontSize: 12 }}>—</span>
          ),
      },
      {
        key: 'status',
        title: t('table.columns.status'),
        dataIndex: 'status',
        width: 110,
        render: (s: TranslationKey['status']) => <StatusBadge kind="content" status={s} />,
      },
      {
        key: 'actions',
        title: '',
        width: 150,
        render: (_: unknown, row: TranslationKey) => (
          <Space>
            <Tooltip title={t('tooltip.addLanguage')}>
              <Button
                size="small"
                icon={<Languages size={14} />}
                aria-label={t('tooltip.addLanguage')}
                onClick={() => openAddLanguage(row)}
              />
            </Tooltip>
            <Tooltip title={t('tooltip.edit')}>
              <Button
                size="small"
                icon={<Pencil size={14} />}
                aria-label={t('tooltip.edit')}
                onClick={() => openEdit(row)}
              />
            </Tooltip>
            <Popconfirm
              title={t('confirm.deleteTitle')}
              description={t('confirm.deleteDefault', { key: row.key })}
              okText={t('common:actions.delete')}
              okButtonProps={{ danger: true, loading: deleteMutation.isPending }}
              cancelText={t('common:actions.cancel')}
              onConfirm={() => deleteMutation.mutate(row)}
            >
              <Tooltip title={t('tooltip.delete')}>
                <Button size="small" danger icon={<Trash2 size={14} />} aria-label={t('tooltip.delete')} />
              </Tooltip>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deleteMutation, t, activeCodes],
  );

  const pickNamespace = (ns: string) => {
    setNamespace(ns);
    setPage(1);
    if (isMobile) setNsDrawerOpen(false);
  };

  const sidebarNamespaces = (
    <div className="ds-ns-filter" role="listbox" aria-label={t('sidebar.ariaLabel')}>
      <Button
        type={namespace === '' ? 'primary' : 'text'}
        size="small"
        block
        style={{ justifyContent: 'flex-start', textAlign: 'left' }}
        onClick={() => pickNamespace('')}
      >
        {t('sidebar.all')}
      </Button>
      {namespaceSummaries.map((ns) => (
        <div key={ns.namespace} style={{ marginTop: 2 }}>
          <Button
            type={namespace === ns.namespace ? 'primary' : 'text'}
            size="small"
            block
            style={{ justifyContent: 'space-between', textAlign: 'left' }}
            onClick={() => pickNamespace(ns.namespace)}
          >
            <span
              style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {ns.namespace}
            </span>
            <Tag style={{ margin: 0 }}>{ns.keyCount}</Tag>
          </Button>
          <div style={{ padding: '2px 8px 0' }}>
            <LanguageCoverageTags present={ns.languageCodes} active={activeCodes} />
          </div>
        </div>
      ))}
    </div>
  );

  const statusOptions: { value: StatusFilter; label: string }[] = [
    { value: 'ALL', label: t('filters.statusAll') },
    { value: 'DRAFT', label: t('status.DRAFT') },
    { value: 'PUBLISHED', label: t('status.PUBLISHED') },
  ];

  const languageOptions = [
    { value: '', label: t('filters.languageAll') },
    ...languages.map((l) => ({ value: l.code, label: `${l.name} (${l.code})` })),
  ];

  return (
    <div data-testid="translations-page">
      <PageHeader
        title={t('page.title')}
        description={t('page.description')}
        breadcrumb={[
          { label: t('common:labels.home'), to: '/' },
          { label: t('breadcrumb.localization') },
          { label: t('breadcrumb.list') },
        ]}
        actions={
          <Space wrap>
            <Button icon={<Settings2 size={14} />} onClick={() => setNsManagerOpen(true)}>
              {t('page.manageNamespaces')}
            </Button>
            <Button type="primary" icon={<Plus size={14} />} onClick={openCreate}>
              {t('page.create')}
            </Button>
          </Space>
        }
      />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : '220px minmax(0, 1fr)',
          gap: 16,
          alignItems: 'flex-start',
        }}
      >
        {isMobile ? (
          <Button icon={<ListFilter size={14} />} onClick={() => setNsDrawerOpen(true)} block>
            {t('sidebar.drawerTrigger')}
            {namespace ? ` · ${namespace}` : ''}
          </Button>
        ) : (
          <Card size="small" style={{ position: 'sticky', top: 16 }} styles={{ body: { padding: 12 } }}>
            <div style={{ marginBottom: 8, fontSize: 12, color: 'var(--ds-text-secondary)' }}>
              {t('sidebar.title')}
            </div>
            {sidebarNamespaces}
          </Card>
        )}

        <Card>
          <DataTable<TranslationKey>
            data={items}
            columns={columns}
            rowKey="id"
            loading={isFetching}
            search={{
              value: search,
              onChange: (v) => {
                setSearch(v);
                setPage(1);
              },
              placeholder: t('table.searchPlaceholder'),
            }}
            filters={
              <Space wrap>
                <Segmented<SortKey>
                  size="small"
                  value={sort}
                  onChange={(v) => {
                    setSort(v);
                    setPage(1);
                  }}
                  options={[
                    { value: 'key', label: t('sort.key') },
                    { value: 'namespace', label: t('sort.namespace') },
                    { value: 'recent', label: t('sort.recent') },
                  ]}
                />
                <Select<string>
                  size="small"
                  style={{ width: 150 }}
                  value={languageFilter}
                  onChange={(v) => {
                    setLanguageFilter(v);
                    setPage(1);
                  }}
                  options={languageOptions}
                />
                {isMobile ? (
                  <Select<StatusFilter>
                    size="small"
                    style={{ width: 130 }}
                    value={statusFilter}
                    onChange={(v) => {
                      setStatusFilter(v);
                      setPage(1);
                    }}
                    options={statusOptions}
                  />
                ) : (
                  <Segmented<StatusFilter>
                    size="small"
                    value={statusFilter}
                    onChange={(v) => {
                      setStatusFilter(v);
                      setPage(1);
                    }}
                    options={statusOptions}
                  />
                )}
              </Space>
            }
            pagination={{
              page,
              size: pageSize,
              total: data?.total ?? 0,
              onChange: (p, s) => {
                setPage(p);
                setPageSize(s);
              },
            }}
            exportable
            exportFileName="translations"
          />
        </Card>
      </div>

      <Drawer
        title={t('sidebar.title')}
        placement="left"
        open={nsDrawerOpen && isMobile}
        onClose={() => setNsDrawerOpen(false)}
        width="80%"
      >
        {sidebarNamespaces}
      </Drawer>

      <TranslationDrawer
        open={drawerOpen}
        translation={editing}
        seed={seed}
        namespaces={namespaceNames}
        onClose={closeDrawer}
      />

      <NamespaceManagerDrawer
        open={nsManagerOpen}
        namespaces={namespaceSummaries}
        loading={nsFetching}
        onClose={() => setNsManagerOpen(false)}
      />
    </div>
  );
}
